import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentIcon from '@mui/icons-material/Payment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

const LegalDocumentation = () => {
  // Sample legal documents - replace with actual data from API
  const legalDocuments = [
    {
      title: 'Registration Certificate',
      description: 'Official registration certificate of GBCN Foundation',
      type: 'PDF',
      size: '1.2 MB',
      date: '2023-01-15',
      url: '#',
    },
    {
      title: 'Memorandum of Association (MOA)',
      description: 'Memorandum of Association outlining the objectives and structure',
      type: 'PDF',
      size: '2.8 MB',
      date: '2023-01-15',
      url: '#',
    },
    {
      title: 'Rules & Regulations',
      description: 'Complete rules and regulations governing the foundation',
      type: 'PDF',
      size: '1.5 MB',
      date: '2023-01-15',
      url: '#',
    },
    {
      title: 'Tax Exemption Certificate',
      description: '80G and 12A certificates for tax exemption',
      type: 'PDF',
      size: '950 KB',
      date: '2023-02-20',
      url: '#',
    },
    {
      title: 'Annual Compliance Report',
      description: 'Annual compliance and audit reports',
      type: 'PDF',
      size: '3.5 MB',
      date: '2024-01-10',
      url: '#',
    },
    {
      title: 'Governing Board Resolution',
      description: 'Recent resolutions and decisions by the governing board',
      type: 'PDF',
      size: '1.1 MB',
      date: '2024-03-15',
      url: '#',
    },
  ];

  const legalSections = [
    {
      title: 'Privacy Policy',
      content: `GBCN Foundation is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.

Information We Collect:
- Personal information (name, email, phone number) when you register or contact us
- Donation information and payment details
- Usage data and cookies for website analytics

How We Use Your Information:
- To provide and maintain our services
- To process donations and memberships
- To send updates and newsletters (with your consent)
- To improve our website and services

Data Security:
We implement appropriate security measures to protect your personal information. However, no method of transmission over the internet is 100% secure.`,
    },
    {
      title: 'Terms & Conditions',
      content: `By accessing and using the GBCN Foundation website, you accept and agree to be bound by the following terms and conditions:

1. Use of Website: The website is provided for informational purposes. You may not use the website for any unlawful purpose.

2. Intellectual Property: All content on this website, including text, graphics, logos, and images, is the property of GBCN Foundation and is protected by copyright laws.

3. Donations: All donations are voluntary and non-refundable. Donations are used for the charitable purposes of the foundation as outlined in our objectives.

4. Limitation of Liability: GBCN Foundation shall not be liable for any indirect, incidental, or consequential damages arising from the use of this website.

5. Modifications: We reserve the right to modify these terms at any time. Continued use of the website constitutes acceptance of modified terms.`,
    },
    {
      title: 'Refund Policy',
      content: `GBCN Foundation operates as a non-profit organization. Please read our refund policy carefully:

1. Donations: Donations made to GBCN Foundation are considered final and non-refundable, as they are used for charitable purposes.

2. Membership Fees: Membership fees are non-refundable once processed. However, if there is a technical error or duplicate payment, please contact us within 7 days.

3. Event Registrations: Event registration fees may be refundable if cancellation is requested at least 48 hours before the event. Refunds will be processed within 14 business days.

4. Contact: For refund inquiries, please contact us at gbcnfoundation@gmail.com with your transaction details.`,
    },
    {
      title: 'Code of Conduct',
      content: `GBCN Foundation expects all members, volunteers, and participants to adhere to the following code of conduct:

1. Respect: Treat all individuals with respect, dignity, and kindness regardless of background, beliefs, or status.

2. Integrity: Act with honesty, transparency, and ethical behavior in all activities.

3. Non-Discrimination: We do not tolerate discrimination based on race, religion, gender, caste, or any other factor.

4. Confidentiality: Respect the confidentiality of sensitive information shared within the organization.

5. Compliance: Follow all applicable laws, regulations, and organizational policies.

Violations of this code may result in termination of membership or participation.`,
    },
  ];

  const handleDownload = (url, name) => {
    if (url !== '#') {
      window.open(url, '_blank');
    } else {
      alert(`Download functionality for "${name}" will be implemented soon.`);
    }
  };

  return (
    <Box sx={{ minHeight: '80vh', py: 8, backgroundColor: '#ffffff' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          component="h1"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: 2,
            color: '#f97316',
            fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' },
          }}
        >
          Legal & Bank
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ textAlign: 'center', mb: 6, maxWidth: '600px', mx: 'auto' }}
        >
          Access official legal documents, policies, and compliance information for GBCN Foundation.
        </Typography>

        {/* Legal Documents Section */}
        <Box sx={{ mb: 8 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <GavelIcon sx={{ fontSize: 40, color: '#f97316', mr: 2 }} />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#111827',
              }}
            >
              Legal Documents
            </Typography>
          </Box>
          <Divider sx={{ mb: 4 }} />
          <Grid container spacing={3}>
            {legalDocuments.map((doc, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(249, 115, 22, 0.15)',
                      borderColor: '#f97316',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
                      <DescriptionIcon sx={{ fontSize: 32, color: '#f97316', mr: 2, mt: 0.5 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, color: '#111827', mb: 1 }}
                        >
                          {doc.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {doc.description}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          <strong>Type:</strong> {doc.type} • <strong>Size:</strong> {doc.size} •{' '}
                          <strong>Date:</strong> {doc.date}
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownload(doc.url, doc.title)}
                          sx={{
                            backgroundColor: '#f97316',
                            '&:hover': { backgroundColor: '#ea580c' },
                          }}
                        >
                          Download
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Policies & Terms Section */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <AccountBalanceIcon sx={{ fontSize: 40, color: '#f97316', mr: 2 }} />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#111827',
              }}
            >
              Policies & Terms
            </Typography>
          </Box>
          <Divider sx={{ mb: 4 }} />
          <Box>
            {legalSections.map((section, index) => (
              <Accordion
                key={index}
                sx={{
                  mb: 2,
                  border: '1px solid #e5e7eb',
                  '&:before': { display: 'none' },
                  '&:hover': {
                    borderColor: '#f97316',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#f97316' }} />}
                  sx={{
                    backgroundColor: '#f9fafb',
                    '&:hover': {
                      backgroundColor: '#fff7ed',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <DescriptionOutlinedIcon sx={{ color: '#f97316', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                      {section.title}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}
                  >
                    {section.content}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>

        {/* Compliance Information */}
        <Box
          sx={{
            p: 4,
            backgroundColor: '#fff7ed',
            borderRadius: 2,
            border: '1px solid #f97316',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <VerifiedUserIcon sx={{ fontSize: 32, color: '#f97316', mr: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
              Compliance & Transparency
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            GBCN Foundation is committed to maintaining the highest standards of legal compliance
            and transparency. All our legal documents are regularly updated and made available to
            members and stakeholders. We operate in accordance with all applicable laws and
            regulations.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            For any questions regarding our legal documentation or compliance matters, please
            contact us at{' '}
            <Box
              component="span"
              sx={{ color: '#f97316', fontWeight: 600 }}
            >
              gbcnfoundation@gmail.com
            </Box>
            {' '}or call{' '}
            <Box
              component="span"
              sx={{ color: '#f97316', fontWeight: 600 }}
            >
              +91-8800656660
            </Box>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default LegalDocumentation;

